import {useQuery} from '@tanstack/react-query';
import {useEffect, useMemo, useState} from 'react';
import {FieldEventPayload, fieldRoundsApi, isFieldSport, isSeriesFormat} from './api';
import {RoundTable, TotalsTable} from './FieldTables';

/**
 * The public face of a field event (Prompt 8): the event page's leaderboard section and the Live
 * View / TV Mode screen. Read-only, from public-field-leaderboard (rounds that have started, names
 * and scores only), refreshed every 20 seconds.
 */
function usePublicField(tournamentUid: string, sport: string | null | undefined) {
    return useQuery<FieldEventPayload>({
        queryKey: ['public-field-leaderboard', tournamentUid],
        queryFn: () => fieldRoundsApi.publicLeaderboard(tournamentUid),
        enabled: !!tournamentUid && isFieldSport(sport),
        refetchInterval: 20_000,
        retry: false,
    });
}

const divisionNameOf = (data: FieldEventPayload) => (k: string | null) => (k ? data.tournament.divisions.find((d) => d.key === k)?.name || k : null);

/** The event page's section: the table across rounds, then the latest round. Nothing until a round starts. */
export function FieldLeaderboardSection({tournamentUid, sport, tone = 'dark'}: {tournamentUid: string; sport: string | null | undefined; tone?: 'light' | 'dark'}) {
    const {data} = usePublicField(tournamentUid, sport);
    if (!data || data.rounds.length === 0) return null;
    const series = isSeriesFormat(data.tournament.format);
    const tables = series && data.series.length ? data.series : data.leaderboard;
    const latest = data.rounds[data.rounds.length - 1];
    const name = divisionNameOf(data);
    return (
        <section className="mt-6 space-y-4" data-testid="public-field-leaderboard">
            <h2 className={tone === 'dark' ? 'font-bebas text-3xl uppercase tracking-wide text-white' : 'text-xl font-bold'}>{series ? 'Order of merit' : 'Leaderboard'}</h2>
            {tables.map((t) => <TotalsTable key={t.division_key ?? '-'} tone={tone} table={t} rounds={data.rounds} kind={series && data.series.length ? 'series' : 'leaderboard'} divisionName={name(t.division_key)} />)}
            {data.rounds.length > 1 && <RoundTable tone={tone} round={latest} divisionName={name(latest.division_key)} />}
        </section>
    );
}

/**
 * Live View and TV Mode. `?tv` (seconds, default 12) turns it into a venue screen that cycles the
 * table across rounds and then each round; otherwise everything is on one scrolling page.
 */
export function FieldLeaderboardLive({event}: {event: any}) {
    const {data, isError} = usePublicField(event?.uid, event?.sport_type);
    const [tv, setTv] = useState<number | null>(null);
    const [panel, setPanel] = useState(0);
    useEffect(() => {
        const raw = new URLSearchParams(window.location.search).get('tv');
        setTv(raw === null ? null : Math.max(5, Number(raw) > 1 ? Number(raw) : 12));
    }, []);

    const panels = useMemo(() => {
        if (!data) return [];
        const name = divisionNameOf(data);
        const series = isSeriesFormat(data.tournament.format) && data.series.length > 0;
        const tables = series ? data.series : data.leaderboard;
        return [
            ...tables.map((t) => <TotalsTable key={`t-${t.division_key}`} big tone="dark" table={t} rounds={data.rounds} kind={series ? 'series' : 'leaderboard'} divisionName={name(t.division_key)} />),
            ...data.rounds.slice().reverse().map((r) => <RoundTable key={r.uid} big tone="dark" round={r} divisionName={name(r.division_key)} />),
        ];
    }, [data]);

    useEffect(() => {
        if (!tv || panels.length < 2) return;
        const t = setInterval(() => setPanel((p) => (p + 1) % panels.length), tv * 1000);
        return () => clearInterval(t);
    }, [tv, panels.length]);

    return (
        <main className="min-h-screen bg-[#06133e] px-4 py-6 text-white lg:px-12" data-testid="field-live">
            <h1 className="font-bebas text-4xl uppercase tracking-wide lg:text-6xl">{event?.title}</h1>
            <p className="mb-6 text-sm text-white/60">Live leaderboard{tv ? ' · TV Mode' : ''}</p>
            {isError && <p className="text-white/70">The leaderboard is not available.</p>}
            {data && data.rounds.length === 0 && <p className="text-white/70">The leaderboard appears once the first round starts.</p>}
            {tv ? panels[panel % Math.max(1, panels.length)] ?? null : <div className="space-y-6">{panels}</div>}
        </main>
    );
}
